import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ETLayout, euro } from "@/pages/et/ETLayout";
import { Receipt, Wallet, AlertTriangle, Percent, CalendarClock, Send, FileCheck, Users, Building2, FileText, FileSpreadsheet, History, CalendarCheck, CheckCircle2, Mail } from "lucide-react";

export default function ETDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [perCentro, setPerCentro] = useState(null);
  const [storico, setStorico] = useState(null);
  const [scad, setScad] = useState(null);

  useEffect(() => {
    api.get("/et/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/et/dashboard/per-centro").then(({ data }) => setPerCentro(data)).catch(() => {});
    api.get("/et/dashboard/storico").then(({ data }) => setStorico(data)).catch(() => {});
    api.get("/et/quote/scadenzario").then(({ data }) => setScad(data)).catch(() => {});
  }, []);

  const generaMese = (mese) => navigate("/easytrasporto/quote", { state: { openGen: mese } });

  const exportMorosi = async (formato) => {
    const mese = perCentro?.mese || d?.mese || "";
    const { data } = await api.get(`/et/export/morosi?competenza=${mese}&formato=${formato}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = `morosi_${mese}.${formato}`; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);
  };

  const boxes = [
    { k: "generato_mese", label: "Generato mese", icon: Receipt, color: "#2563EB", money: true },
    { k: "incassato_mese", label: "Incassato mese", icon: Wallet, color: "#10B981", money: true },
    { k: "morosi_mese", label: "Morosi mese", icon: AlertTriangle, color: "#EF4444" },
    { k: "percentuale_incasso", label: "% incasso", icon: Percent, color: "#8B5CF6", pct: true },
    { k: "quote_da_incassare", label: "Quote da incassare", icon: CalendarClock, color: "#F97316", to: "/easytrasporto/quote" },
    { k: "solleciti_da_inviare", label: "Solleciti da inviare", icon: Send, color: "#EF4444", to: "/easytrasporto/quote" },
    { k: "ricevute_da_inviare", label: "Ricevute da inviare", icon: FileCheck, color: "#10B981", to: "/easytrasporto/ricevute" },
    { k: "beneficiari_attivi", label: "Beneficiari attivi", icon: Users, color: "#2563EB", to: "/easytrasporto/beneficiari" },
  ];

  return (
    <ETLayout subtitle={`Competenza ${d?.mese || ""}`} title="Dashboard">
      {scad && scad.mancanti_mese_corrente > 0 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          data-testid="et-scad-banner"
          className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-heading text-amber-900">Quote del mese {scad.mese_corrente} da generare</div>
              <div className="text-sm text-amber-700">Ci sono <b>{scad.mancanti_mese_corrente}</b> beneficiari attivi senza quota per il mese corrente.</div>
            </div>
          </div>
          <button data-testid="et-scad-genera-corrente" onClick={() => generaMese(scad.mese_corrente)}
            className="brand-gradient text-white border-0 px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2">
            <CalendarCheck className="h-4 w-4" /> Genera ora
          </button>
        </motion.div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {boxes.map((b, i) => {
          const Icon = b.icon;
          const val = d ? (b.money ? euro(d[b.k]) : b.pct ? `${d[b.k]}%` : d[b.k]) : "—";
          return (
            <motion.button
              key={b.k} data-testid={`et-box-${b.k}`}
              onClick={() => b.to && navigate(b.to)}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="text-left rounded-2xl p-5 text-white shadow-md hover:-translate-y-1 transition-transform"
              style={{ backgroundColor: b.color }}
            >
              <Icon className="h-6 w-6 opacity-80 mb-6" />
              <div className="text-2xl font-heading font-bold">{val}</div>
              <div className="text-sm opacity-90 mt-1">{b.label}</div>
            </motion.button>
          );
        })}
      </div>

      {d && d.beneficiari_senza_email > 0 && (
        <button data-testid="et-senza-email-indicatore" onClick={() => navigate("/easytrasporto/beneficiari")}
          className="mb-6 w-full rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex items-center justify-between gap-3 hover:bg-sky-100 transition-colors text-left">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-sky-600 shrink-0" />
            <span className="text-sm text-sky-900"><b>{d.beneficiari_senza_email}</b> beneficiari attivi senza email — necessaria per gli invii automatici (ricevute/solleciti).</span>
          </div>
          <span className="text-xs font-medium text-sky-700 whitespace-nowrap">Importa email →</span>
        </button>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mb-6" data-testid="et-scadenzario-card">
        <div className="flex items-center gap-2 mb-1">
          <CalendarCheck className="h-5 w-5 text-brand-green" />
          <h3 className="font-heading text-lg text-slate-900">Stato generazione quote</h3>
          <span className="text-xs text-slate-400">· mese corrente + mesi recenti</span>
        </div>
        <p className="text-xs text-slate-400 mb-4">Verifica a colpo d'occhio se le quote di ogni mese sono state generate per tutti i beneficiari attivi.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {(scad?.righe || []).map((r) => (
            <div key={r.competenza} data-testid={`et-scad-row-${r.competenza}`}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 ${r.mancanti > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-100 bg-emerald-50"}`}>
              <div>
                <div className="font-medium text-slate-800 flex items-center gap-2">
                  {r.competenza}
                  {r.is_corrente && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">corrente</span>}
                </div>
                <div className="text-xs text-slate-500">{r.quote_generate}/{r.beneficiari_attivi} generate{r.mancanti > 0 ? ` · ${r.mancanti} mancanti` : ""}</div>
              </div>
              {r.mancanti > 0 ? (
                <button data-testid={`et-scad-genera-${r.competenza}`} onClick={() => generaMese(r.competenza)}
                  className="text-sm px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors flex items-center gap-1.5">
                  <CalendarCheck className="h-3.5 w-3.5" /> Genera
                </button>
              ) : (
                <span className="text-emerald-600 flex items-center gap-1 text-sm"><CheckCircle2 className="h-4 w-4" /> Completo</span>
              )}
            </div>
          ))}
          {(!scad || scad.righe.length === 0) && (
            <div className="text-slate-400 text-sm py-4">Nessun dato disponibile.</div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mb-6" data-testid="et-storico-card">
        <div className="flex items-center gap-2 mb-1">
          <History className="h-5 w-5 text-slate-500" />
          <h3 className="font-heading text-lg text-slate-900">Mesi precedenti (storico)</h3>
          <span className="text-xs text-slate-400">· esclude il mese in corso ({storico?.mese_corrente || d?.mese || ""})</span>
        </div>
        <p className="text-xs text-slate-400 mb-4">Riepilogo consolidato dei mesi già chiusi, per una lettura reale dell'incassato indipendente dal mese appena avviato.</p>
        {storico?.totale && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MiniStat label="Generato" value={euro(storico.totale.generato)} />
            <MiniStat label="Incassato" value={euro(storico.totale.incassato)} accent="text-emerald-600" />
            <MiniStat label="Residuo" value={euro(storico.totale.residuo)} accent="text-red-600" />
            <MiniStat label="% incasso" value={`${storico.totale.percentuale}%`} accent="text-violet-600" />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="text-left font-medium py-2">Competenza</th>
                <th className="text-right font-medium py-2">Quote</th>
                <th className="text-right font-medium py-2">Generato</th>
                <th className="text-right font-medium py-2">Incassato</th>
                <th className="text-right font-medium py-2">Residuo</th>
                <th className="text-right font-medium py-2">Morosi</th>
                <th className="text-right font-medium py-2">% Incasso</th>
              </tr>
            </thead>
            <tbody>
              {(storico?.per_mese || []).map((m) => (
                <tr key={m.competenza} data-testid={`et-storico-row-${m.competenza}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2.5 text-slate-800 font-medium">{m.competenza}</td>
                  <td className="py-2.5 text-right font-mono text-slate-600">{m.quote}</td>
                  <td className="py-2.5 text-right font-mono">{euro(m.generato)}</td>
                  <td className="py-2.5 text-right font-mono text-emerald-600">{euro(m.incassato)}</td>
                  <td className="py-2.5 text-right font-mono text-red-600">{euro(m.residuo)}</td>
                  <td className="py-2.5 text-right font-mono">{m.morosi}</td>
                  <td className="py-2.5 text-right font-mono text-slate-500">{m.percentuale}%</td>
                </tr>
              ))}
              {(!storico || storico.per_mese.length === 0) && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400">Nessun mese precedente disponibile.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mb-6" data-testid="et-per-centro-card">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <h3 className="font-heading text-lg text-slate-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-brand-green" /> Riepilogo incassi per centro
            <span className="text-xs text-slate-400 font-normal">· competenza {perCentro?.mese || d?.mese || ""}</span>
          </h3>
          <div className="flex gap-2">
            <button data-testid="et-export-morosi-pdf" onClick={() => exportMorosi("pdf")}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
              <FileText className="h-4 w-4" /> Morosi PDF
            </button>
            <button data-testid="et-export-morosi-xlsx" onClick={() => exportMorosi("xlsx")}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4" /> Morosi Excel
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="text-left font-medium py-2">Centro</th>
                <th className="text-right font-medium py-2">Quote</th>
                <th className="text-right font-medium py-2">Generato</th>
                <th className="text-right font-medium py-2">Incassato</th>
                <th className="text-right font-medium py-2">Residuo</th>
                <th className="text-right font-medium py-2">Morosi</th>
                <th className="text-right font-medium py-2">% Incasso</th>
              </tr>
            </thead>
            <tbody>
              {(perCentro?.centri || []).map((c) => (
                <tr key={c.centro_name} data-testid={`et-centro-row-${c.centro_name}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2.5 text-slate-800">{c.centro_name}</td>
                  <td className="py-2.5 text-right font-mono text-slate-600">{c.quote}</td>
                  <td className="py-2.5 text-right font-mono">{euro(c.generato)}</td>
                  <td className="py-2.5 text-right font-mono text-emerald-600">{euro(c.incassato)}</td>
                  <td className="py-2.5 text-right font-mono text-red-600">{euro(c.residuo)}</td>
                  <td className="py-2.5 text-right font-mono">{c.morosi}</td>
                  <td className="py-2.5 text-right font-mono text-slate-500">{c.percentuale}%</td>
                </tr>
              ))}
              {perCentro?.totale && (
                <tr className="font-semibold bg-slate-50">
                  <td className="py-2.5">TOTALE</td>
                  <td className="py-2.5 text-right font-mono">{perCentro.totale.quote}</td>
                  <td className="py-2.5 text-right font-mono">{euro(perCentro.totale.generato)}</td>
                  <td className="py-2.5 text-right font-mono text-emerald-700">{euro(perCentro.totale.incassato)}</td>
                  <td className="py-2.5 text-right font-mono text-red-700">{euro(perCentro.totale.residuo)}</td>
                  <td className="py-2.5 text-right font-mono">{perCentro.totale.morosi}</td>
                  <td className="py-2.5 text-right font-mono">—</td>
                </tr>
              )}
              {(!perCentro || perCentro.centri.length === 0) && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400">Nessun dato per la competenza corrente.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <h3 className="font-heading text-lg text-slate-900 mb-4">📋 Oggi devi fare</h3>
        <div className="space-y-2">
          <Task n={d?.quote_da_incassare} label="quote da incassare" onClick={() => navigate("/easytrasporto/quote")} color="#F97316" />
          <Task n={d?.ricevute_da_inviare} label="ricevute da inviare" onClick={() => navigate("/easytrasporto/ricevute")} color="#10B981" />
          <Task n={d?.solleciti_da_inviare} label="solleciti da inviare" onClick={() => navigate("/easytrasporto/quote")} color="#EF4444" />
        </div>
      </div>
    </ETLayout>
  );
}

const Task = ({ n, label, onClick, color }) => (
  <button onClick={onClick} data-testid={`et-task-${label.replace(/\s/g, "-")}`}
    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-left">
    <span className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: color }}>{n ?? 0}</span>
    <span className="text-slate-700">{label}</span>
  </button>
);

const MiniStat = ({ label, value, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`font-heading text-lg font-semibold mt-0.5 ${accent}`}>{value}</div>
  </div>
);
